import { cva, type VariantProps } from 'class-variance-authority';
import {
  Button as AriaButton,
  ButtonProps as AriaButtonProps,
  Link as AriaLink,
  ToggleButton as AriaToggleButton,
  ToggleButtonProps as AriaToggleButtonProps,
} from 'react-aria-components';
import { cn } from '~/lib/utils';
import Icon from './Icon';

export const variants = cva(
  [
    'inline-flex items-center justify-center rounded-full outline-none transition-colors transition-shadow duration-100 border border-1 select-none cursor-pointer',
    'focus:ring-0',
    'disabled:pointer-events-none disabled:opacity-40',
    'data-pending:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        solid: [
          'text-button-primary-text border-button-primary-border bg-linear-to-r from-button-primary-background1 to-button-primary-background2 shadow-button',
          'hover:brightness-110 hover:shadow-button-hover',
          'active:from-button-primary-background3 active:to-button-primary-background4 active:border-button-primary-border active:inset-ring-2 active:inset-ring-button-primary-border active:shadow-none',
          'data-pending:border-0 data-pending:inset-ring-0 data-pending:bg-none data-pending:bg-button-background-pending',
        ],
        outlined: [
          'text-button-outlined-text bg-button-outlined-background border-button-outlined-border',
          'hover:border-button-outlined-hover-border hover:bg-button-outlined-hover-background',
          'active:border-button-outlined-pressed-border active:bg-button-outlined-pressed-background',
        ],
        ghost: 'text-foreground bg-transparent border-0',
        secondary:
          'text-button-secondary-text bg-button-secondary-background border-0 hover:bg-button-secondary-hover',
        danger:
          'text-button-danger-text bg-button-danger-background border-button-danger-border hover:bg-button-danger-hover',
        icon: 'text-foreground p-0 bg-transparent border-0',
      },
      size: {
        min: 'px-0 py-0',
        xs: 'px-4 h-8 text-[13px]',
        sm: 'px-5 h-10 text-[14px] font-semibold leading-5',
        md: 'px-6 h-11 text-[15px] font-semibold leading-5',
        inherit: '',
      },
      isLocked: {
        true: 'pointer-events-none ',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'solid',
      size: 'md',
    },
  },
);

export interface ButtonProps extends AriaButtonProps, VariantProps<typeof variants> {
  className?: string;
  children?: React.ReactNode;
  isLocked?: boolean;
  to?: string;
  asToggle?: boolean;
}

const Button = ({
  className,
  children,
  variant,
  size,
  isPending,
  isDisabled,
  isLocked,
  to,
  asToggle,
  ...props
}: ButtonProps) => {
  const _className = cn(
    variants({
      variant,
      size,
      isLocked,
      className,
    }),
  );

  if (to) {
    return (
      <AriaLink href={to} className={_className} isDisabled={isDisabled}>
        {children}
      </AriaLink>
    );
  }

  if (asToggle) {
    return (
      <AriaToggleButton
        {...(props as AriaToggleButtonProps)}
        isDisabled={isDisabled}
        className={_className}
      >
        {children}
      </AriaToggleButton>
    );
  }

  return (
    <AriaButton {...props} isDisabled={isDisabled} className={_className}>
      {children}
      {isPending && <Icon name="sync" className="ml-3 text-[18px] animate-spin" />}
    </AriaButton>
  );
};

export default Button;
